import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiUsers, FiArrowLeft, FiCopy, FiCheck, FiCheckCircle, FiShare2 } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CreateGroupForm from '../components/groups/CreateGroupForm';
import ShareGroupInviteModal from '../components/groups/ShareGroupInviteModal';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

function CreateGroup({ onAddGroup }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [createdGroup, setCreatedGroup] = useState(null);
  const [inviteCodeCopied, setInviteCodeCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const handleSuccess = async (data) => {
    setCreatedGroup(data);
    if (onAddGroup) await onAddGroup();
  };

  const copyInviteCode = async () => {
    if (createdGroup?.invite_code) {
      try {
        await navigator.clipboard.writeText(createdGroup.invite_code);
        setInviteCodeCopied(true);
        setTimeout(() => setInviteCodeCopied(false), 2000);
        toast.success('Invite code copied to clipboard');
      } catch (err) {
        console.error('Failed to copy invite code:', err);
        toast.error('Failed to copy invite code. Please try again.');
      }
    }
  };

  if (createdGroup) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-4 safe-area-inset-top">
        <Helmet>
          <title>Group Created - TeeSync</title>
        </Helmet>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-md w-full"
        >
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-status-success/10 rounded-lg flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-status-success" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">Group created</h1>
            <p className="text-text-secondary text-sm">
              Send the invite link to your golf buddies
            </p>
          </div>

          <div className="bg-surface rounded-lg border border-border-subtle shadow-card p-6">
            <div className="pb-5 border-b border-border-subtle">
              <p className="text-xs text-text-secondary mb-1">Group name</p>
              <p className="text-base font-semibold text-text-primary truncate">
                {createdGroup.name}
              </p>
            </div>

            <div className="py-5 border-b border-border-subtle">
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-3">
                Invite code
              </p>
              <div className="flex items-center gap-3">
                <code className="flex-1 font-mono tracking-widest text-2xl font-bold text-text-primary bg-app rounded-md border border-border-subtle px-4 py-3 text-center">
                  {createdGroup.invite_code}
                </code>
                <button
                  onClick={copyInviteCode}
                  className="flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium flex-shrink-0"
                >
                  <SafeIcon icon={inviteCodeCopied ? FiCheck : FiCopy} className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{inviteCodeCopied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="py-5 border-b border-border-subtle">
              <h3 className="text-sm font-medium text-text-secondary mb-3">How to share</h3>
              <ul className="text-sm text-text-secondary space-y-2">
                <li className="flex gap-2"><span className="text-accent">•</span><span>Send the invite link to your friends</span></li>
                <li className="flex gap-2"><span className="text-accent">•</span><span>It names the group and gets them the app if they need it</span></li>
                <li className="flex gap-2"><span className="text-accent">•</span><span>The code and link stay available in group settings</span></li>
              </ul>
            </div>

            <div className="pt-5 space-y-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium"
              >
                <SafeIcon icon={FiShare2} className="w-4 h-4 mr-2" />
                Share invite link
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-center px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-app transition-colors font-medium"
              >
                <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                Go to Dashboard
              </button>
            </div>
          </div>
        </motion.div>

        <ShareGroupInviteModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          inviteCode={createdGroup.invite_code}
          groupName={createdGroup.name}
          ownerName={user?.user_metadata?.full_name}
          surface="create_group"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <Helmet>
        <title>Create Group - TeeSync</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-accent hover:text-accent mb-6 group transition-all duration-200"
            >
              <SafeIcon
                icon={FiArrowLeft}
                className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200"
              />
              <span className="font-semibold">Back to Dashboard</span>
            </button>
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-3">
              Create New Golf Group
            </h1>
            <p className="text-base text-text-secondary">
              Create a group and get an invite code to share with your golf buddies.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-surface rounded-lg border border-border-subtle shadow-card p-6 sm:p-8"
          >
            <CreateGroupForm
              onSuccess={handleSuccess}
              onCancel={() => navigate('/')}
              submitLabel="Create Group"
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default CreateGroup;
